import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "./data";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[rgba(253,251,247,0.72)] backdrop-blur-xl border-b border-border py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between">
        <button
          data-testid="logo-home-button"
          onClick={() => go("hero")}
          className="flex items-center group"
        >
          <img
            src="/prism-logo-full.png"
            alt="Prism Psychology"
            className="h-9 sm:h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="text-sm tracking-wide text-text-secondary hover:text-primary transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              {n.label}
            </button>
          ))}
          <button
            data-testid="header-book-cta"
            onClick={() => go("booking")}
            className="rounded-full border border-primary/80 px-6 py-2.5 text-sm text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
          >
            Book a session
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-primary"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[rgba(253,251,247,0.96)] backdrop-blur-xl border-t border-border"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => go(n.id)}
                  className="text-left font-serif text-2xl text-primary"
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export { scrollTo };
